import {
  Children,
  Fragment,
  JSX,
  memo,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import RenderIfVisible from "react-render-if-visible";
import { HighlightColours } from "../models/HighlightColors";
import { PageSpans } from "../models/Page";
import Span from "../models/Span";
import "../styles/highlightedSpans.css";

export {
  // display and style highlighted span rows for document pages
  HighlightedSpanRows,
  // display and style highlighted spans for document pages
  HighlightedSpans,
  SpansHighlightableSkeleton,
};

type Props = {
  /**
   * Page IDs
   */
  pageIds: string[];
  getPageSpansById: (pageId: string) => Promise<PageSpans>;
  /**
   * Span IDs to highlight as a group.
   * For example [ ['1','2'], ['3'] ] would highlight the space between Span 1 and 2, but not the space betweeen 2
   * and 3.
   */
  selectedSpans?: string[][];
  /**
   * Highlight color of each Span group
   */
  groupColors?: string[];
};

const useSpanHighlightStylesheet = (
  styleId: string,
  selectedSpans?: string[][],
  groupColors?: string[],
) => {
  //
  const [groupedStyles, setGroupedStyles] = useState<string[]>([]);
  // update Span highlights when higlighted spans change
  useEffect(() => {
    const spanStyles = (selectedSpans ?? []).flatMap((spans, groupIndex) => {
      // get custom span group color
      const spanColor =
        (groupColors ?? [])[groupIndex] ??
        HighlightColours.getHighlightColor(groupIndex);

      // #<SpansHiglightable/> [data-span=UUID] { background-color: '' }
      // #<SpansHiglightable/> [data-span=UUID] span:first-child { background-color: '' }
      const spanStyle = spans.map(
        (s, i) =>
          `#${styleId} [data-span="${s}"] ${i == spans.length ? "span:first-child" : ""} { background-color: ${spanColor}; }`,
      );
      return spanStyle;
    });

    setGroupedStyles(spanStyles);
  }, [groupColors, selectedSpans]);

  // add styles on mount/unmount
  useEffect(() => {
    let sheet = document.createElement("style");
    sheet.innerHTML = groupedStyles.join("\n");
    sheet = document.body.appendChild(sheet);
    return function () {
      document.body.removeChild(sheet);
    };
  });
};

/**
 * Add and detach stylesheet that creates the following style for each table start,soft/hard stop rows
 * <code>
 * #paramStyleId [data-span-row="4"] {
 *  background-color: rowColor;
 * }
 * </code>
 * @param styleId id to insert in all stylesheet definitions
 * @param highlightedRows array of entries or null. example entry below
 *          pageId: PG0, rowNumber: 4, colorIndex: 1
 */

const useSpanRowHighlightStylesheet = (
  styleId: string,
  highlightedRows:
    | { pageId: string; rowNumber: number; colorIndex: number }[]
    | null,
) => {
  //
  const [groupedStyles, setGroupedStyles] = useState<string[]>([]);
  // update Span highlights when higlighted spans change
  useEffect(() => {
    const rowStyles = (highlightedRows ?? []).flatMap((spans, groupIndex) => {
      // get custom span group color
      const rowColor = HighlightColours.getHighlightColor(spans.colorIndex);
      const rowStyle = `#${styleId} [data-page="${spans.pageId}"][data-span-row="${spans.rowNumber}"]>div.rowNumber+span { background-color: ${rowColor}; }`;
      return rowStyle;
    });

    setGroupedStyles(rowStyles);
  }, [highlightedRows]);

  // add styles on mount/unmount
  useEffect(() => {
    let sheet = document.createElement("style");
    sheet.innerHTML = groupedStyles.join("\n");
    sheet = document.body.appendChild(sheet);
    return function () {
      document.body.removeChild(sheet);
    };
  });
};

const ESTIMATED_SPAN_HEIGHT: number = 16 * 1 * 8;
/**
 * New Component structure
 *
 * Top level component that accepts rendering information like selected spans, styles, etc
 * ? Defered component to stop pages from loading, render 1 page at a time
 * Page level component that accepts prop to load page spans. page spans should be stored in a ref
 * ? Defered component to delay span rendering
 *
 * Unsure where deferred should be
 */
const HighlightedSpans = (props: Props) => {
  const styleId = useRef(
    ("asdf" + Math.random() + "s").replace(".", ""),
  ).current;
  useSpanHighlightStylesheet(styleId, props.selectedSpans, props.groupColors);

  return (
    <div id={styleId} className={"highlightedSpans"} tabIndex={2}>
      {props.pageIds.map((x, i) => (
        <Fragment key={i}>
          <span className={"pageId"}>Page {i + 1}</span>
          <RenderIfVisible
            defaultHeight={ESTIMATED_SPAN_HEIGHT}
            initialVisible={i < 4}
            key={i}
            rootElementClass="pageBreak"
            rootElement="div"
            placeholderElementClass="pageBreak skeleton"
            placeholderElement="div"
          >
            <SmartPageSpansComp
              key={i}
              spanChunkSize={400}
              pageId={x}
              pageIndex={i}
              getPageSpansById={props.getPageSpansById}
            />
          </RenderIfVisible>
        </Fragment>
      ))}
    </div>
  );
};

const SpansHighlightableSkeleton = () => {
  return (
    <div className={"highlightedSpans skeleton"}>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div>
  );
};
function SmartPageSpansSkeleton() {
  return (
    <>
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((x) => (
        <div key={x} className={"skeleton"}>
          {x}
        </div>
      ))}
    </>
  );
}
SmartPageSpansSkeleton.displayName = "SmartPageSpansSkeleton";

type SmartPageSpansProps = {
  spanChunkSize: number;
  pageIndex: number;
  pageId: string;
  getPageSpansById: (pageId: string) => Promise<PageSpans>;
};
const SmartPageSpansComp = memo(
  (props: SmartPageSpansProps) => {
    const [spans, setSpans] = useState<Span[] | undefined>();

    useEffect(() => {
      let mounted = true;
      if (!spans) {
        console.log(`Loading spans for ${props.pageId}`);
        props.getPageSpansById(props.pageId).then((pageSpans) => {
          if (mounted) {
            setSpans(pageSpans.spans);
          }
        });
      }
      return function () {
        mounted = false;
      };
    }, []);

    // Defer span loading
    if (spans) {
      return (
        <Defer chunkSize={props.spanChunkSize}>
          {spans.map((span) => (
            <span key={span.id} data-span={span.id}>
              <span>{span.unicodeSafeValue}</span>
              <span> </span>
            </span>
          ))}
        </Defer>
      );
    }
    // return place holder
    return (
      <>
        {" "}
        <SmartPageSpansSkeleton />{" "}
      </>
    );
  },
  (prev, next) => {
    return (
      prev.getPageSpansById === next.getPageSpansById &&
      prev.pageId === next.pageId
    );
  },
);

// TODO: document black magic https://itnext.io/improving-slow-mounts-in-react-apps-cff5117696dc
type DeferProps = { chunkSize: number; children: ReactNode };
const Defer = ({ chunkSize, children }: DeferProps): JSX.Element => {
  const [renderedItemsCount, setRenderedItemsCount] = useState(chunkSize);

  const childrenArray = useMemo(() => Children.toArray(children), [children]);

  useEffect(() => {
    if (renderedItemsCount < childrenArray.length) {
      window.requestIdleCallback(
        () => {
          setRenderedItemsCount(
            Math.min(renderedItemsCount + chunkSize, childrenArray.length),
          );
        },
        { timeout: 200 },
      );
    }
  }, [renderedItemsCount, childrenArray.length, chunkSize]);

  return childrenArray.slice(0, renderedItemsCount) as unknown as JSX.Element;
};

const ESTIMATED_SPAN_ROW_HEIGHT: number = 16 * 1 * 50;
/**
 * New Component structure
 *
 * Top level component that accepts rendering information like selected spans, styles, etc
 * ? Defered component to stop pages from loading, render 1 page at a time
 * Page level component that accepts prop to load page spans. page spans should be stored in a ref
 * ? Defered component to delay span rendering
 *
 * Unsure where deferred should be
 */
type HighlightedSpanRowsProps = {
  /**
   * Page IDs
   */
  pageIds: string[];
  getPageSpansById: (pageId: string) => Promise<PageSpans>;
  highlightedRows:
    | { pageId: string; rowNumber: number; colorIndex: number }[]
    | null;
};
const HighlightedSpanRows = (props: HighlightedSpanRowsProps) => {
  const styleId = useRef(
    ("asdf" + Math.random() + "s").replace(".", ""),
  ).current;
  useSpanRowHighlightStylesheet(styleId, props.highlightedRows);

  return (
    <div id={styleId} className={"highlightedSpans highlightedSpanRows"}>
      {props.pageIds.map((x, i) => (
        <Fragment key={i}>
          <div className={"pageId"}>
            <div>Page {i + 1}</div>
          </div>
          <RenderIfVisible
            defaultHeight={ESTIMATED_SPAN_ROW_HEIGHT}
            stayRendered
            initialVisible={i < 4}
            placeholderElementClass={"pageBreak skeleton"}
          >
            <SmartPageSpansRowComp
              spanChunkSize={200}
              pageId={x}
              pageIndex={i}
              getPageSpansById={props.getPageSpansById}
            />
          </RenderIfVisible>
        </Fragment>
      ))}
    </div>
  );
};

type SmartPageSpansRowProps = {
  spanChunkSize: number;
  pageIndex: number;
  pageId: string;
  getPageSpansById: (pageId: string) => Promise<PageSpans>;
};
const SmartPageSpansRowComp = memo(
  (props: SmartPageSpansRowProps) => {
    const [spanRows, setSpanRows] = useState<Span[][] | undefined>();

    useEffect(() => {
      let mounted = true;
      if (!spanRows) {
        props.getPageSpansById(props.pageId).then((pageSpans) => {
          if (mounted) {
            setSpanRows(
              pageSpans.spans.reduce((prev, curr, index, arr) => {
                if (
                  prev.length > 0 &&
                  prev[prev.length - 1][0].rowNumber === curr.rowNumber
                ) {
                  prev[prev.length - 1].push(curr);
                } else {
                  prev.push([curr]);
                }
                return prev;
              }, [] as Span[][]),
            );
          }
        });
      }
      return function () {
        mounted = false;
      };
    }, []);

    // Defer span loading
    if (spanRows) {
      return (
        <Defer chunkSize={props.spanChunkSize}>
          {spanRows.map((row, i) => (
            <Fragment key={i}>
              <div
                data-page={row[0].pageId ?? ""}
                data-span-row={row[0]?.rowNumber ?? ""}
              >
                <div className={"rowNumber"} style={{ minWidth: "5rem" }}>
                  {`Row ${row.length === 0 ? "???" : row[0].rowNumber + 1}`}
                </div>
                <span>
                  {row.map((span) => (
                    <span key={span.id} data-span={span.id}>
                      {/* NOTE: intentional trailing space  */}
                      {`${span.unicodeSafeValue} `}
                    </span>
                  ))}
                </span>
              </div>
            </Fragment>
          ))}
        </Defer>
      );
    }
    // return place holder
    return (
      <tr className="skeleton">
        <td></td>
        <td></td>
      </tr>
    );
  },
  (prev, next) => {
    return (
      prev.getPageSpansById === next.getPageSpansById &&
      prev.pageId === next.pageId
    );
  },
);
